import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProject } from '../../../context/ProjectContext';
import { useToast } from '../../../context/ToastContext';
import { supabase } from '../../../lib/supabase';
import { 
  ArrowLeftIcon, 
  CheckCircleIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowLeftCircleIcon, 
  ArrowRightCircleIcon,
  DocumentDuplicateIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  verticalListSortingStrategy as verticalStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import debounce from 'lodash/debounce';
import GenerateWithCliveModal from './GenerateWithCliveModal';
import { executeDataTableGeneration, importDataTableGeneration } from '../../agents/documentAi';
import { useUser } from '../../../context/UserContext';

const EMPTY_ROWS_COUNT = 1;
const SAVE_INDICATOR_TIMEOUT = 2000;
const MIN_COLUMN_WIDTH = 100;
const noSelectClass = "select-none";

export default function ViewTable() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const { addToast } = useToast();
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const editInputRef = useRef(null);
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const saveTimeoutRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const [resizingColumn, setResizingColumn] = useState(null);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, column: null });
  const [editingColumnName, setEditingColumnName] = useState(null);
  const [columnsReady, setColumnsReady] = useState(false);
  const [editingTableName, setEditingTableName] = useState(false);
  const [rowContextMenu, setRowContextMenu] = useState({ 
    visible: false, 
    x: 0, 
    y: 0, 
    rowIndex: null 
  });
  const { user } = useUser();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Add this constant for table width calculation
  const TABLE_CONTAINER_REF = React.createRef();

  // Add new state for total width
  const [totalWidth, setTotalWidth] = useState(0);

  // Add useEffect to initialize total width
  useEffect(() => {
    if (TABLE_CONTAINER_REF.current) {
      setTotalWidth(TABLE_CONTAINER_REF.current.offsetWidth);
      
      // Add resize observer to update total width when window resizes
      const resizeObserver = new ResizeObserver(entries => {
        setTotalWidth(entries[0].contentRect.width);
      });
      
      resizeObserver.observe(TABLE_CONTAINER_REF.current);
      return () => resizeObserver.disconnect();
    }
  }, []);

  const SortableColumnHeader = React.memo(({ column, children }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: column.id });

    const width = columnWidths[column.name] || MIN_COLUMN_WIDTH;
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      width: `${width}px`,
      minWidth: `${MIN_COLUMN_WIDTH}px`,
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <th
        ref={setNodeRef}
        style={style}
        className={`px-4 py-3 text-left text-sm font-medium border-b border-divider relative group ${noSelectClass}`}
        {...attributes}
        {...listeners}
        onContextMenu={(e) => handleContextMenu(e, column)}
      >
        {children}
      </th>
    );
  });

  const columns = React.useMemo(() => {
    if (!table?.column_order || !table?.dataset || table.dataset.length === 0) return [];
    
    // Get all unique column names from the dataset
    const datasetColumns = new Set();
    table.dataset.forEach(row => {
      Object.keys(row).forEach(key => datasetColumns.add(key));
    });

    // Get existing columns from column_order
    const existingColumns = table.column_order.map(col => {
      if (typeof col === 'string') {
        try {
          return JSON.parse(col);
        } catch {
          return {
            id: `col-${col.toLowerCase().replace(/\s+/g, '-')}`,
            name: col
          };
        }
      }
      return col;
    });

    // Get existing column names
    const existingColumnNames = new Set(existingColumns.map(col => col.name));

    // Create new columns for those in dataset but not in column_order
    const newColumns = Array.from(datasetColumns)
      .filter(colName => !existingColumnNames.has(colName))
      .map(colName => ({
        id: `col-${colName.toLowerCase().replace(/\s+/g, '-')}`,
        name: colName
      }));

    // If we found new columns, update the database
    if (newColumns.length > 0) {
      const updatedColumns = [...existingColumns, ...newColumns];
      
      // Update the database asynchronously
      (async () => {
        try {
          const { error } = await supabase
            .from('data_tables')
            .update({ column_order: updatedColumns })
            .eq('id', id);

          if (error) {
            console.error('Error updating column order:', error);
          }
        } catch (error) {
          console.error('Error updating column order:', error);
        }
      })();

      return updatedColumns;
    }

    return existingColumns;
  }, [table?.column_order, table?.dataset, id]);

  useEffect(() => {
    loadTable();
  }, [id]);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingCell]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu({ visible: false, x: 0, y: 0, column: null });
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [contextMenu.visible]);

  useEffect(() => {
    if (columns.length > 0) {
      setColumnsReady(true);
    }
  }, [columns]);

  const loadTable = async () => {
    try {
      const { data, error } = await supabase
        .from('data_tables')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      console.log('Raw data from database:', data);
      console.log('Column order:', data.column_order);

      // Get all unique column names from the dataset
      const datasetColumns = new Set();
      if (data.dataset && data.dataset.length > 0) {
        data.dataset.forEach(row => {
          Object.keys(row).forEach(key => datasetColumns.add(key));
        });
      }

      // Initialize or parse existing column_order
      let parsedColumns = [];
      if (data.column_order && Array.isArray(data.column_order)) {
        parsedColumns = data.column_order;
      }

      // Create column objects for any missing columns
      const existingColumnNames = new Set(parsedColumns.map(col => 
        typeof col === 'string' ? col : col.name
      ));

      const newColumns = Array.from(datasetColumns)
        .filter(colName => !existingColumnNames.has(colName))
        .map(colName => ({
          id: `col-${colName.toLowerCase().replace(/\s+/g, '-')}`,
          name: colName
        }));

      // Combine existing and new columns
      const updatedColumns = [...parsedColumns, ...newColumns];

      // Update the database if we added new columns
      if (newColumns.length > 0) {
        const { error: updateError } = await supabase
          .from('data_tables')
          .update({ column_order: updatedColumns })
          .eq('id', id);

        if (updateError) {
          console.error('Error updating column order:', updateError);
        }
      }

      setTable({
        ...data,
        column_order: updatedColumns
      });
    } catch (error) {
      console.error('Error loading table:', error);
      addToast('Error loading table', 'error');
      navigate('/content/documents');
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (rowIndex, column) => {
    setEditingCell({ rowIndex, column: column.name });
    setTempInputValue(table.dataset[rowIndex]?.[column.name] || '');
  };

  // Add a debounced save function using useCallback
  const debouncedSave = useCallback(
    debounce(async (newDataset) => {
      try {
        const { error } = await supabase
          .from('data_tables')
          .update({ dataset: newDataset })
          .eq('id', id);

        if (error) throw error;
        setSaveStatus('saved');
        
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(() => {
          setSaveStatus('');
        }, SAVE_INDICATOR_TIMEOUT);

      } catch (error) {
        console.error('Error saving changes:', error);
        setSaveStatus('error');
      }
    }, 1000),
    [id]
  );

  // Update handleCellChange to only update local state
  const handleCellChange = (rowIndex, column, value) => {
    const newDataset = [...table.dataset];
    
    if (!newDataset[rowIndex]) {
      newDataset[rowIndex] = columns.reduce((obj, col) => ({
        ...obj,
        [col.name]: ''
      }), {});
    }

    newDataset[rowIndex][column] = value;

    // Only update local state, no saving
    setTable(prevTable => ({
      ...prevTable,
      dataset: newDataset
    }));
  };

  // Add new function to save changes
  const saveChanges = async (rowIndex, column, value) => {
    try {
      setSaveStatus('saving');
      const newDataset = [...table.dataset];
      
      if (!newDataset[rowIndex]) {
        newDataset[rowIndex] = columns.reduce((obj, col) => ({
          ...obj,
          [col.name]: ''
        }), {});
      }

      newDataset[rowIndex][column] = value;

      // Reorder all row data to match column order
      const orderedDataset = newDataset.map(row => {
        return columns.reduce((orderedRow, col) => ({
          ...orderedRow,
          [col.name]: row[col.name] || ''
        }), {});
      });

      // Remove empty rows from the end before saving
      const cleanDataset = orderedDataset.filter(row => 
        Object.values(row).some(val => val !== '')
      );

      const { error } = await supabase
        .from('data_tables')
        .update({ dataset: cleanDataset })
        .eq('id', id);

      if (error) throw error;

      setSaveStatus('saved');
      
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      saveTimeoutRef.current = setTimeout(() => {
        setSaveStatus('');
      }, SAVE_INDICATOR_TIMEOUT);

    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveStatus('error');
    }
  };

  const handleKeyDown = (e, rowIndex, column) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      const value = e.target.value;
      handleCellChange(rowIndex, column, value);

      const currentColIndex = columns.findIndex(col => col.name === column);
      let nextColIndex, nextRowIndex;
      
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (currentColIndex === 0) {
            nextColIndex = columns.length - 1;
            nextRowIndex = rowIndex - 1;
          } else {
            nextColIndex = currentColIndex - 1;
            nextRowIndex = rowIndex;
          }
        } else {
          if (currentColIndex === columns.length - 1) {
            nextColIndex = 0;
            nextRowIndex = rowIndex + 1;
          } else {
            nextColIndex = currentColIndex + 1;
            nextRowIndex = rowIndex;
          }
        }
      } else if (e.key === 'Enter') {
        nextColIndex = currentColIndex;
        nextRowIndex = rowIndex + 1;
      }

      if (nextRowIndex >= table.dataset.length) {
        const newDataset = [...table.dataset];
        newDataset[nextRowIndex] = Object.fromEntries(
          columns.map(col => [col.name, ''])
        );
        setTable({ ...table, dataset: newDataset });
      }

      if (nextRowIndex >= 0) {
        setTimeout(() => {
          setEditingCell({
            rowIndex: nextRowIndex,
            column: columns[nextColIndex].name
          });
        }, 0);
      }
    }
  };

  // Add column management functions
  const handleAddColumn = async () => {
    const newColumnId = `col-${Date.now()}`;
    const newColumnName = `Column ${columns.length + 1}`;
    const newColumn = { id: newColumnId, name: newColumnName };
    const updatedColumns = [...columns, newColumn];
    
    try {
      const { error } = await supabase
        .from('data_tables')
        .update({
          column_order: updatedColumns,
          dataset: table.dataset.map(row => ({
            ...row,
            [newColumnName]: ''
          }))
        })
        .eq('id', id);

      if (error) throw error;
      
      setTable({
        ...table,
        column_order: updatedColumns,
        dataset: table.dataset.map(row => ({
          ...row,
          [newColumnName]: ''
        }))
      });
    } catch (error) {
      console.error('Error adding column:', error);
      addToast('Failed to add column', 'error');
    }
  };

  const handleColumnReorder = async (result) => {
    if (!result.destination) return;

    try {
      const sourceIndex = result.source.index;
      const destinationIndex = result.destination.index;
      
      const newColumns = Array.from(columns);
      const [reorderedColumn] = newColumns.splice(sourceIndex, 1);
      newColumns.splice(destinationIndex, 0, reorderedColumn);

      // Reorder data according to new column order
      const orderedDataset = table.dataset.map(row => {
        return newColumns.reduce((orderedRow, col) => ({
          ...orderedRow,
          [col.name]: row[col.name] || ''
        }), {});
      });

      setTable(prevTable => ({
        ...prevTable,
        column_order: newColumns,
        dataset: orderedDataset
      }));

      const { error } = await supabase
        .from('data_tables')
        .update({ 
          column_order: newColumns,
          dataset: orderedDataset
        })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error reordering columns:', error);
      addToast('Failed to reorder columns', 'error');
      setTable(prevTable => ({
        ...prevTable,
        column_order: columns
      }));
    }
  };

  const handleResizeStart = (e, column) => {
    setIsResizing(true);
    setStartX(e.pageX);
    setStartWidth(columnWidths[column.name] || MIN_COLUMN_WIDTH);
    setResizingColumn(column.name);
  };

  const handleResizeMove = (e) => {
    if (!isResizing) return;

    const diff = e.pageX - startX;
    const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + diff);
    
    // Only update if we're not at the minimum width, or if we're increasing
    if (newWidth > MIN_COLUMN_WIDTH || diff > 0) {
      const widthDiff = newWidth - (columnWidths[resizingColumn] || MIN_COLUMN_WIDTH);
      
      // Calculate the total width of other columns
      const otherColumnsWidth = columns.reduce((total, col) => {
        if (col.name === resizingColumn) return total;
        return total + (columnWidths[col.name] || MIN_COLUMN_WIDTH);
      }, 0);

      // Calculate the proportion each other column should adjust
      const newColumnWidths = { ...columnWidths };
      newColumnWidths[resizingColumn] = newWidth;

      // Only adjust other columns if we have space to work with
      if (otherColumnsWidth > MIN_COLUMN_WIDTH * (columns.length - 1)) {
        columns.forEach(col => {
          if (col.name !== resizingColumn) {
            const currentWidth = columnWidths[col.name] || MIN_COLUMN_WIDTH;
            const proportion = currentWidth / otherColumnsWidth;
            const adjustment = widthDiff * proportion;
            const adjustedWidth = Math.max(MIN_COLUMN_WIDTH, currentWidth - adjustment);
            newColumnWidths[col.name] = adjustedWidth;
          }
        });
      }

      setColumnWidths(newColumnWidths);
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    setResizingColumn(null);
  };

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [isResizing]);

  const handleContextMenu = (e, column) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY,
      column
    });
  };

  const handleRenameColumn = async (column, newName) => {
    if (!newName.trim() || newName === column.name) {
      setEditingColumnName(null);
      return;
    }

    try {
      const newColumns = columns.map(col => 
        col.id === column.id ? { ...col, name: newName } : col
      );
      
      const newDataset = table.dataset.map(row => {
        const newRow = { ...row };
        newRow[newName] = row[column.name];
        delete newRow[column.name];
        return newRow;
      });

      const { error } = await supabase
        .from('data_tables')
        .update({
          column_order: newColumns,
          dataset: newDataset
        })
        .eq('id', id);

      if (error) throw error;

      setTable({
        ...table,
        column_order: newColumns,
        dataset: newDataset
      });
    } catch (error) {
      console.error('Error renaming column:', error);
      addToast('Failed to rename column', 'error');
    } finally {
      setEditingColumnName(null);
    }
  };

  const handleTableNameChange = async (newName) => {
    if (!newName.trim() || newName === table.name) {
      setEditingTableName(false);
      return;
    }

    try {
      const { error } = await supabase
        .from('data_tables')
        .update({ name: newName.trim() })
        .eq('id', id);

      if (error) throw error;

      setTable({
        ...table,
        name: newName.trim()
      });
    } catch (error) {
      console.error('Error renaming table:', error);
      addToast('Failed to rename table', 'error');
    } finally {
      setEditingTableName(false);
    }
  };

  // Add new function to handle column deletion
  const handleDeleteColumn = async (column) => {
    try {
      const newColumns = columns.filter(col => col.id !== column.id);
      const newDataset = table.dataset.map(row => {
        const newRow = { ...row };
        delete newRow[column.name];
        return newRow;
      });

      const { error } = await supabase
        .from('data_tables')
        .update({
          column_order: newColumns,
          dataset: newDataset
        })
        .eq('id', id);

      if (error) throw error;

      setTable({
        ...table,
        column_order: newColumns,
        dataset: newDataset
      });
    } catch (error) {
      console.error('Error deleting column:', error);
      addToast('Failed to delete column', 'error');
    }
  };

  // First, add a RowNumberHeader component inside ViewTable
  const RowNumberHeader = () => (
    <th 
      className="px-4 py-3 text-left text-sm font-medium border-b border-divider bg-dark-300 w-16 text-gray-400"
      style={{ width: '4rem' }}
    >
      #
    </th>
  );

  // Then update the TableHeader component to include the row number column
  const TableHeader = React.memo(() => {
    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      })
    );

    const handleDragEnd = (event) => {
      const { active, over } = event;
      
      if (active.id !== over.id) {
        const oldIndex = columns.findIndex((col) => col.id === active.id);
        const newIndex = columns.findIndex((col) => col.id === over.id);
        
        handleColumnReorder({
          source: { index: oldIndex },
          destination: { index: newIndex }
        });
      }
    };

    return (
      <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
        <tr className="bg-dark-300">
          <RowNumberHeader />
          <SortableContext
            items={columns.map(col => col.id)}
            strategy={horizontalListSortingStrategy}
          >
            {columns.map((column) => (
              <SortableColumnHeader key={column.id} column={column}>
                <div className="flex items-center gap-2">
                  {editingColumnName?.id === column.id ? (
                    <input
                      type="text"
                      className="bg-dark-100 px-2 py-1 rounded w-full"
                      defaultValue={column.name}
                      autoFocus
                      onBlur={(e) => handleRenameColumn(column, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameColumn(column, e.target.value);
                        } else if (e.key === 'Escape') {
                          setEditingColumnName(null);
                        }
                      }}
                    />
                  ) : (
                    <>
                      <span className="flex-1">{column.name}</span>
                    </>
                  )}
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize bg-accent-primary/20 opacity-0 group-hover:opacity-100"
                    onMouseDown={(e) => handleResizeStart(e, column)}
                  />
                </div>
              </SortableColumnHeader>
            ))}
          </SortableContext>
        </tr>
      </DndContext>
    );
  });

  // Add new function to handle adding column relative to current column
  const handleAddColumnRelative = async (column, position) => {
    const newColumnId = `col-${Date.now()}`;
    const newColumnName = `Column ${columns.length + 1}`;
    const newColumn = { id: newColumnId, name: newColumnName };
    
    try {
      const currentIndex = columns.findIndex(col => col.id === column.id);
      const newColumns = [...columns];
      newColumns.splice(position === 'right' ? currentIndex + 1 : currentIndex, 0, newColumn);
      
      const { error } = await supabase
        .from('data_tables')
        .update({
          column_order: newColumns,
          dataset: table.dataset.map(row => ({
            ...row,
            [newColumnName]: ''
          }))
        })
        .eq('id', id);

      if (error) throw error;
      
      setTable({
        ...table,
        column_order: newColumns,
        dataset: table.dataset.map(row => ({
          ...row,
          [newColumnName]: ''
        }))
      });
    } catch (error) {
      console.error('Error adding column:', error);
      addToast('Failed to add column', 'error');
    }
  };

  const handleRowContextMenu = (e, rowIndex) => {
    e.preventDefault();
    setRowContextMenu({
      visible: true,
      x: e.pageX,
      y: e.pageY,
      rowIndex
    });
  };

  const handleDuplicateRow = async (rowIndex) => {
    try {
      const newDataset = [...table.dataset];
      const rowToDuplicate = { ...newDataset[rowIndex] };
      newDataset.splice(rowIndex + 1, 0, rowToDuplicate);

      const { error } = await supabase
        .from('data_tables')
        .update({ dataset: newDataset })
        .eq('id', id);

      if (error) throw error;

      setTable({
        ...table,
        dataset: newDataset
      });
    } catch (error) {
      console.error('Error duplicating row:', error);
      addToast('Failed to duplicate row', 'error');
    }
  };

  const handleDeleteRow = async (rowIndex) => {
    try {
      const newDataset = [...table.dataset];
      newDataset.splice(rowIndex, 1);

      const { error } = await supabase
        .from('data_tables')
        .update({ dataset: newDataset })
        .eq('id', id);

      if (error) throw error;

      setTable({
        ...table,
        dataset: newDataset
      });
    } catch (error) {
      console.error('Error deleting row:', error);
      addToast('Failed to delete row', 'error');
    }
  };

  useEffect(() => {
    const handleClickOutside = () => {
      setRowContextMenu({ visible: false, x: 0, y: 0, rowIndex: null });
    };

    if (rowContextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [rowContextMenu.visible]);

  // Add new state for temporary input value
  const [tempInputValue, setTempInputValue] = useState('');

  // Update the TableBody component
  const TableBody = React.memo(({ 
    displayData, 
    columns, 
    onRowReorder,
    onCellClick,
    onCellChange,
    onKeyDown,
    editingCell,
    editInputRef 
  }) => {
    // Move SortableRow inside TableBody
    const SortableRow = React.memo(({ row, rowIndex, children }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id: `row-${rowIndex}` });

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
      };

      return (
        <tr
          ref={setNodeRef}
          style={style}
          className="border-b border-divider last:border-b-0 hover:bg-dark-300 group"
          onContextMenu={(e) => handleRowContextMenu(e, rowIndex)}
        >
          <td 
            {...attributes}
            {...listeners}
            className="px-4 py-3 text-sm text-gray-400 select-none cursor-move hover:bg-dark-400"
          >
            {rowIndex + 1}
          </td>
          {children}
        </tr>
      );
    });

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          delay: 50,
          tolerance: 5,
        },
      })
    );

    return (
      <DndContext 
        sensors={sensors}
        onDragEnd={onRowReorder}
      >
        <tbody>
          <SortableContext
            items={displayData.map((_, index) => `row-${index}`)}
            strategy={verticalStrategy}
          >
            {displayData.map((row, rowIndex) => (
              <SortableRow 
                key={`row-${rowIndex}`} 
                row={row} 
                rowIndex={rowIndex}
              >
                {columns.map(column => (
                  <td
                    key={column.id}
                    className="px-4 py-3 text-sm relative"
                    onClick={() => onCellClick(rowIndex, column)}
                  >
                    {editingCell?.rowIndex === rowIndex && 
                     editingCell?.column === column.name ? (
                      <input
                        ref={editInputRef}
                        type="text"
                        className="absolute inset-0 w-full h-full px-4 py-3 bg-dark-100 border-2 border-accent-primary focus:outline-none"
                        defaultValue={row[column.name] || ''}
                        onBlur={(e) => {
                          handleCellChange(rowIndex, column.name, e.target.value);
                          saveChanges(rowIndex, column.name, e.target.value);
                          setEditingCell(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            handleCellChange(rowIndex, column.name, e.target.value);
                            saveChanges(rowIndex, column.name, e.target.value);
                            handleKeyDown(e, rowIndex, column.name);
                          } else if (e.key === 'Escape') {
                            setEditingCell(null);
                          }
                        }}
                      />
                    ) : (
                      <span className="block min-h-[1.5rem]">
                        {row[column.name] || ''}
                      </span>
                    )}
                  </td>
                ))}
              </SortableRow>
            ))}
          </SortableContext>
        </tbody>
      </DndContext>
    );
  });

  // Add a handler for row reordering
  const handleRowReorder = async (result) => {
    if (!result.active || !result.over) return;

    const oldIndex = parseInt(result.active.id.replace('row-', ''));
    const newIndex = parseInt(result.over.id.replace('row-', ''));

    if (oldIndex === newIndex) return;

    try {
      const newDataset = [...table.dataset];
      const [movedRow] = newDataset.splice(oldIndex, 1);
      newDataset.splice(newIndex, 0, movedRow);

      // Update local state first for immediate feedback
      setTable(prevTable => ({
        ...prevTable,
        dataset: newDataset
      }));

      // Then update the database
      const { error } = await supabase
        .from('data_tables')
        .update({ dataset: newDataset })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error reordering rows:', error);
      addToast('Failed to reorder rows', 'error');
    }
  };

  const handleGenerate = async (settings) => {
    try {
      setIsGenerating(true);

      const result = await executeDataTableGeneration({
        settings, 
        projectId: currentProject.id, 
        organizationId: currentProject.organization_id, 
        userId: user.id,
        referenceDocs: settings.referenceDocs,
        tableData: {
          column_order: table.column_order,
          dataset: table.dataset
        }
      });

      if (result.success) {
        // Update the table with the generated data
        setTable(prevTable => ({
          ...prevTable,
          dataset: result.output
        }));

        // Save to database
        const { error } = await supabase
          .from('data_tables')
          .update({ 
            dataset: result.output,
            updated_at: new Date().toISOString(),
            updated_by: user.id
          })
          .eq('id', id);

        if (error) throw error;
        addToast('Generation completed successfully', 'success');
      } else {
        addToast('Generation failed: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Generation error:', error);
      addToast('Generation failed', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFileImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsImporting(true);
      const text = await file.text();
      
      // Parse CSV
      const rows = text.split('\n');
      const headers = rows[0].split(',').map(h => h.trim());
      const data = rows.slice(1).map(row => {
        const values = row.split(',').map(v => v.trim());
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index] || '';
          return obj;
        }, {});
      });

      const result = await importDataTableGeneration({
        settings: {
          agent: 'dave',
          prompt: `Review and import this CSV data, matching columns where possible and adding new ones if needed. CSV Headers: ${headers.join(', ')}`
        },
        projectId: currentProject.id,
        organizationId: currentProject.organization_id,
        userId: user.id,
        tableData: {
          column_order: table.column_order,
          dataset: table.dataset,
          importData: data
        }
      });

      if (result.success) {
        // Update the table with the imported data
        setTable(prevTable => ({
          ...prevTable,
          dataset: result.output.dataset,
          column_order: result.output.column_order
        }));

        // Save to database
        const { error } = await supabase
          .from('data_tables')
          .update({ 
            dataset: result.output.dataset,
            column_order: result.output.column_order,
            updated_at: new Date().toISOString(),
            updated_by: user.id
          })
          .eq('id', id);

        if (error) throw error;
        addToast('Data imported successfully', 'success');
      } else {
        addToast('Import failed: ' + result.error, 'error');
      }
    } catch (error) {
      console.error('Error importing data:', error);
      addToast('Failed to import data', 'error');
    } finally {
      setIsImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!table) {
    return <div>Table not found</div>;
  }

  const displayData = [
    ...table.dataset,
    ...Array(EMPTY_ROWS_COUNT).fill().map(() => 
      Object.fromEntries(columns.map(col => [col.name, '']))
    )
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/content/documents')}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="w-6 h-6" />
          </button>
          {editingTableName ? (
            <input
              type="text"
              className="text-2xl font-bold bg-dark-100 px-2 py-1 rounded"
              defaultValue={table.name}
              autoFocus
              onBlur={(e) => handleTableNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleTableNameChange(e.target.value);
                } else if (e.key === 'Escape') {
                  setEditingTableName(false);
                }
              }}
            />
          ) : (
            <h1 
              className="text-2xl font-bold cursor-pointer hover:text-accent-primary"
              onClick={() => setEditingTableName(true)}
            >
              {table.name}
            </h1>
          )}
        </div>

        <div className="flex items-center gap-4">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv"
            className="hidden"
            onChange={handleFileImport}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-dark-300 text-white rounded hover:bg-dark-400 transition-colors flex items-center gap-2"
            disabled={isImporting}
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            Import CSV
          </button>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 transition-colors flex items-center gap-2"
          >
            Generate with Clive
          </button>
          {/* Save Status Indicator */}
          <div className="flex items-center gap-2">
            {saveStatus === 'saving' && (
              <span className="text-sm text-gray-400">Saving...</span>
            )}
            {saveStatus === 'saved' && (
              <div className="flex items-center gap-1 text-green-500">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-sm">Saved</span>
              </div>
            )}
            {saveStatus === 'error' && (
              <span className="text-sm text-red-500">Failed to save</span>
            )}
          </div>
        </div>
      </div>

      <div 
        ref={TABLE_CONTAINER_REF} 
        className="bg-dark-200 rounded-lg overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <TableHeader />
            </thead>
            <TableBody 
              displayData={displayData}
              columns={columns}
              onRowReorder={handleRowReorder}
              onCellClick={handleCellClick}
              onCellChange={handleCellChange}
              onKeyDown={handleKeyDown}
              editingCell={editingCell}
              editInputRef={editInputRef}
            />
          </table>
        </div>
      </div>

      {contextMenu.visible && (
        <div
          className="fixed bg-dark-200 rounded-lg shadow-lg py-1 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-dark-300 flex items-center gap-2"
            onClick={() => {
              setEditingColumnName(contextMenu.column);
              setContextMenu({ visible: false, x: 0, y: 0, column: null });
            }}
          >
            <PencilIcon className="w-4 h-4" />
            Rename Column
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-dark-300 flex items-center gap-2"
            onClick={() => {
              handleAddColumnRelative(contextMenu.column, 'left');
              setContextMenu({ visible: false, x: 0, y: 0, column: null });
            }}
          >
            <ArrowLeftCircleIcon className="w-4 h-4" />
            Add Column Left
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-dark-300 flex items-center gap-2"
            onClick={() => {
              handleAddColumnRelative(contextMenu.column, 'right');
              setContextMenu({ visible: false, x: 0, y: 0, column: null });
            }}
          >
            <ArrowRightCircleIcon className="w-4 h-4" />
            Add Column Right
          </button>
          {columns.length > 1 && (
            <button
              className={`w-full px-4 py-2 text-left flex items-center gap-2 text-red-500 border-t border-divider ${
                columns.length > 1 ? 'hover:bg-dark-300' : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => {
                if (columns.length > 1) {
                  handleDeleteColumn(contextMenu.column);
                  setContextMenu({ visible: false, x: 0, y: 0, column: null });
                }
              }}
              title={columns.length === 1 ? "Cannot delete the last column" : ""}
            >
              <TrashIcon className="w-4 h-4" />
              Delete Column
            </button>
          )}
        </div>
      )}

      {rowContextMenu.visible && (
        <div
          className="fixed bg-dark-200 rounded-lg shadow-lg py-1 z-50"
          style={{ top: rowContextMenu.y, left: rowContextMenu.x }}
        >
          <button
            className="w-full px-4 py-2 text-left hover:bg-dark-300 flex items-center gap-2"
            onClick={() => {
              handleDuplicateRow(rowContextMenu.rowIndex);
              setRowContextMenu({ visible: false, x: 0, y: 0, rowIndex: null });
            }}
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
            Duplicate Row
          </button>
          <button
            className="w-full px-4 py-2 text-left hover:bg-dark-300 flex items-center gap-2 text-red-500 border-t border-divider"
            onClick={() => {
              handleDeleteRow(rowContextMenu.rowIndex);
              setRowContextMenu({ visible: false, x: 0, y: 0, rowIndex: null });
            }}
          >
            <TrashIcon className="w-4 h-4" />
            Delete Row
          </button>
        </div>
      )}

      {isGenerating && (
        <div className="absolute inset-0 bg-dark-100/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-dark-200 rounded-lg p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
            <div className="text-lg font-medium">Generating content...</div>
            <div className="text-sm text-gray-400">This may take a few moments</div>
          </div>
        </div>
      )}

      {isImporting && (
        <div className="absolute inset-0 bg-dark-100/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-dark-200 rounded-lg p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-primary"></div>
            <div className="text-lg font-medium">Importing data...</div>
            <div className="text-sm text-gray-400">This may take a few moments</div>
          </div>
        </div>
      )}

      <GenerateWithCliveModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerate}
      />
    </div>
  );
} 